import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as S from '@/components/Ad/Complaint/Complaint.styles';
import { Popup } from '@/components/UI/Popup/Popup';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { FlagFilledIcon, FlagIcon } from '@/components/UI/Icons/Icons';
import { getLoginUrlWithReturn, sleep } from '@/utils/helpers';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { Textarea } from '@/components/UI/Textarea/Textarea';
import { GetReportReasonsResponse, ReportReason } from '@/api/types/ads.types';
import { CREATE_REPORT } from '@/api/mutations/ads.mutations';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { QUERY } from '@/constants/common';
import { useAuthStore } from '@/store/useAuthStore';
import { useAdDetails } from '@/hooks/useAdDetails';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { CreateReportResponse } from '@/api/types/common.types';
import { useServerDataContext } from '@/contexts';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { GET_REPORT_REASONS } from '@/api/queries/ads.queries';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { ModalSheet } from '@/components/UI/ModalSheet';
import { useResponsive } from '@/hooks/useResponsive';

export const Complaint = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const customPush = useCustomPush();
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const [isPopupOpen, popupToggle] = useToggleOpenState();
  const [isModalOpen, toggleModal] = useToggleOpenState(QUERY.complain);
  const [stage, setStage] = useState<'reportReason' | 'text'>('reportReason');
  const [reportReason, setReportReason] = useState<ReportReason | null>(null);
  const [reportReasonId, setReportReasonId] = useState('');
  const [body, setBody] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [isAuthenticated] = useAuthStore(state => [state.isAuthenticated]);
  const [errorMsg, setErrorMsg] = useState('');
  const { adDetails, refetch } = useAdDetails();
  const { currentCategory } = useCurrentCategory();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const isReportStage = stage === 'reportReason';
  const isTextStage = stage === 'text';

  const { data: reportReasonsData, loading: isReportReasonsLoading } = useQuery<
    GetReportReasonsResponse,
    { categoryId?: string }
  >(GET_REPORT_REASONS, {
    variables: {
      categoryId: currentCategory?.id,
    },
    skip: !isModalOpen || !currentCategory?.id,
  });

  const reportReasons: ReportReason[] = reportReasonsData?.reportReasons ?? [];
  const { id, isReportAvailable } = adData ?? {};

  const [createReport, { loading }] = useMutation<CreateReportResponse>(
    CREATE_REPORT,
    {
      onCompleted: () => {
        refetch();
        toggleModal();
        sleep(MODAL_ANIMATION_DURATION).then(() => popupToggle());
      },
      onError: error => {
        setErrorMsg(error.graphQLErrors?.[0]?.message);
      },
    }
  );

  function startComplain() {
    if (!isAuthenticated) {
      const cleanPath = router.asPath.split('#')[0];

      customPush(
        getLoginUrlWithReturn(`${cleanPath}#${QUERY.complain}`, router.asPath)
      );

      return;
    }
    toggleModal();
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!id) return;

    createReport({
      variables: {
        adId: id,
        reportReasonId,
        body,
      },
    });
  }

  function selectReasonAndNext(rr: ReportReason) {
    setReportReason(rr);
    setReportReasonId(rr.id);
    setStage('text');
  }

  function resetForm() {
    setReportReasonId('');
    setBody('');
  }

  function goBack() {
    setStage('reportReason');
    setErrorMsg('');
    resetForm();
  }

  function handleClose() {
    toggleModal();
    sleep(MODAL_ANIMATION_DURATION).then(() => setErrorMsg(''));
  }

  const reportReasonsView = useMemo(() => {
    return isReportReasonsLoading || !reportReasons.length
      ? Array.from({ length: 6 }, (_, index) => (
          <S.SkeletonItem key={`complaint-reason-skeleton-${index}`}>
            <SkeletonLoader width="55%" height="18px" borderRadius={6} />
          </S.SkeletonItem>
        ))
      : reportReasons.map(reason => (
          <Checkbox
            onChangeHook={() => selectReasonAndNext(reason)}
            key={reason.id}
            value={reportReasonId === reason.id}
            label={reason.name}
            appearance="radio"
            size="lg"
            hasBorderBottom={false}
            dataTestId={AdPageTestIds.complaint.reportReason}
          />
        ));
  }, [isReportReasonsLoading, reportReasons]);

  useEffect(() => {
    (async () => {
      if (!isModalOpen) {
        await sleep(500);
        setStage('reportReason');
        resetForm();
      }
    })();
  }, [isModalOpen]);

  if (!adData) return null;

  return (
    <>
      <S.ComplaintButton
        data-testid={AdPageTestIds.complaint.box}
        hasPadding={false}
        appearance="transparent"
      >
        {isReportAvailable ? (
          <FlagIcon fill={theme.palette.primary.labels} />
        ) : (
          <FlagFilledIcon fill={theme.palette.system.red} />
        )}
        <Typography.Body
          data-testid={AdPageTestIds.complaint.title}
          $variant="2"
          onClick={isReportAvailable ? startComplain : undefined}
        >
          {isReportAvailable
            ? t('pages.ad.complain')
            : t('pages.ad.complained')}
        </Typography.Body>
      </S.ComplaintButton>
      <ModalSheet
        isOpen={isModalOpen}
        onClose={handleClose}
        isBodyScrollLocked={!isDesktop}
        title={isReportStage ? t('pages.ad.complaint_reason') : ''}
      >
        <form onSubmit={onSubmit}>
          {isReportStage ? reportReasonsView : null}

          {isTextStage && reportReason && (
            <>
              <S.ComplaintHead>
                <S.ComplaintHeadButton onClick={goBack} disabled={loading}>
                  <S.ComplaintBackIcon />
                </S.ComplaintHeadButton>
                <S.ComplaintTitle>{reportReason.name}</S.ComplaintTitle>
                <S.ComplaintHeadButton onClick={handleClose} disabled={loading}>
                  <S.ComplaintCloseIcon />
                </S.ComplaintHeadButton>
              </S.ComplaintHead>
              <Textarea
                appearance="quartenary"
                value={body}
                onChange={e => setBody(e.target.value)}
                ref={bodyRef}
                autoFocus
                onBlur={() => bodyRef.current?.focus()}
                placeholder={t('pages.ad.complaint_placeholder')}
                height={150}
                data-testid={AdPageTestIds.complaint.textarea}
                hasError={!!errorMsg}
              />
              {!!errorMsg && <S.ComplaintError>{errorMsg}</S.ComplaintError>}
              <S.ComplaintSubmit
                type="submit"
                appearance="primary"
                disabled={loading}
                data-testid={AdPageTestIds.complaint.submit}
              >
                {t('common.send')}
              </S.ComplaintSubmit>
            </>
          )}
        </form>
      </ModalSheet>
      <Popup
        dataTestId={AdPageTestIds.complaint.popup}
        isOpen={isPopupOpen}
        close={popupToggle}
        variant="1"
        size="xlarge"
        isBodyScrollLocked={!isDesktop}
      >
        <S.PopupContainer>
          <IconBox
            $filename="check-circle-rounded.svg"
            $width={35}
            $height={35}
            $iconWidth={35}
            $iconHeight={35}
          />
          <S.PopupTextContainer>
            <S.PopupTitle data-testid={CommonTestIds.popup.title}>
              {t('pages.ad.complaint_sent')}
            </S.PopupTitle>
            <S.PopupDescription data-testid={CommonTestIds.popup.description}>
              {t('pages.ad.complaint_sent_subtitle')}
            </S.PopupDescription>
          </S.PopupTextContainer>
          <S.PopupButton
            appearance="secondary"
            onClick={popupToggle}
            data-testid={CommonTestIds.popup.actionLeft}
          >
            {t('common.got_it')}
          </S.PopupButton>
        </S.PopupContainer>
      </Popup>
    </>
  );
};
